import DataViewSelectorListItem from "./DataViewSelectorListItem";
import QueryEditor from "./QueryEditor";
import { QueryMeta } from "@avenueops/shared-types";
import { SmartForm } from "@avenueops/web-components";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { DevTool } from "@hookform/devtools";
import { useMonaco } from "@monaco-editor/react";
import { ConnectionType, FilterMatchType, RowFilter, WorkflowTriggerType, EnrichmentJoinType } from "@prisma/client";
import VisualQueryEditor from "components/visual-query-editor/VisualQueryEditor";
import { useQueryColumns, useQueryRunContext } from "context/QueryRunContext";
import { AnimatePresence, motion } from "framer-motion";
import _ from "lodash";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { getConnectionChoices } from "utils/getConnectionChoices";
import { trpc, trpc2 } from "utils/trpc";
import RowFilterList from "./rowFilters/RowFilterList";
import { TableauQueryMeta } from "./TableauQueryMeta";

interface Props {
  prefix?: string;
  visualQueryEditor?: boolean;
  isEnrichment?: boolean;
  readOnly?: boolean;
  disableRowFilters?: boolean;
  disableDataViewImport?: boolean;
  isFullScreen?: boolean;
  previewColumnsSelectable?: boolean;
  setIsFullScreen?: (_: boolean) => void;
}

export default function QueryBuilder({
  prefix = "",
  visualQueryEditor = false,
  isEnrichment = false,
  readOnly = false,
  disableRowFilters = false,
  disableDataViewImport = false,
  isFullScreen,
  setIsFullScreen,
  previewColumnsSelectable,
}: Props) {
  const fieldPrefix = prefix ? `${prefix}.` : "";
  const { control, watch, setValue, register, getFieldState, formState } = useFormContext();

  const monaco = useMonaco();
  const utils = trpc.useContext();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { mutateAsync: previewQuery, data: previewData, isLoading: isLoadingPreview } = trpc.dataviews.preview.useMutation();
  const { data: connections, isLoading: isLoadingConnections } = trpc2.v2.databaseConnections.list.useQuery({});
  const { data: dataViews, isLoading: dataViewsLoading } = trpc.dataviews.list.useQuery();
  const [queryRunContext, _setQueryRunContext] = useQueryRunContext();

  const queryColumns = useQueryColumns();

  // Register non-interactable fields
  register(`${fieldPrefix}queryMeta`);
  if (!disableDataViewImport) {
    register(`${fieldPrefix}workflowTriggerType`);
  }
  // Watch form values
  const databaseId = watch(`${fieldPrefix}databaseId`);

  const { error: queryError } = getFieldState(`${fieldPrefix}query`, formState);

  const queryAreaErrors = queryError?.message;

  const runPreview = async (
    databaseId: string,
    query: string,
    queryMeta?: QueryMeta,
    rowFilters?: Omit<RowFilter, "id" | "workflowId">[],
    rowFilterMatchType?: FilterMatchType,
    originalRows?: any[],
  ) => {
    const { resultRows, dataviewQueryDurationMs, totalNumRows } = await previewQuery({
      databaseId,
      query,
      queryMeta,
      rowFilters,
      rowFilterMatchType,
      queryContext: queryRunContext,
    });

    if (resultRows.length === 0 || _.isEmpty(resultRows[0])) {
      throw new Error("The query returned no results. Please try again.");
    }
    return { resultRows, dataviewQueryDurationMs, totalNumRows };
  };

  if (isLoadingConnections) {
    return (
      <div className="flex">
        <Skeleton />
      </div>
    );
  }

  if (visualQueryEditor) {
    return (
      <div className="relative">
        <VisualQueryEditor datasourceIdName={`${fieldPrefix}databaseId`} prefix={`${fieldPrefix}visualQueryBlob`} connections={connections ?? []} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <DevTool control={control} placement={"bottom-right"} />
      <div>
        <div className="py-2 flex gap-2">
          <div className="flex-1"></div>
          {connections && connections.find((connection) => connection.id === databaseId)?.connectionType === ConnectionType.GOOGLE_SHEETS ? (
            <div className="flex items-center gap-1 ml-2">
              <SmartForm.Toggle name={`${fieldPrefix}queryMeta.includeHeaders`} title="Include Headers" disabled={readOnly} />
            </div>
          ) : null}
          {connections && connections.find((connection) => connection.id === databaseId)?.connectionType === ConnectionType.MONGODB ? (
            <div className="flex items-center gap-1 ml-2">
              <SmartForm.Toggle name={`${fieldPrefix}queryMeta.isAggregation`} title="Aggregation" disabled={readOnly} />
            </div>
          ) : null}

          {isLoadingConnections || !connections ? (
            <Skeleton className="h-8 my-6" />
          ) : (
            <>
              <div className={`min-w-[185px]`}>
                <>
                  <SmartForm.DropdownSelect
                    key="databaseIdSelector"
                    name={`${fieldPrefix}databaseId`}
                    options={getConnectionChoices(connections)}
                    placeholder="Select a database..."
                    disableUnselect
                    disabled={readOnly}
                    size="MEDIUM"
                  />
                  {connections && connections.find((connection) => connection.id === databaseId)?.connectionType === ConnectionType.TABLEAU ? (
                    <TableauQueryMeta connectionId={databaseId} prefix={`${fieldPrefix}queryMeta`} />
                  ) : null}
                </>
              </div>
            </>
          )}
        </div>
        <div
          className={`pt-10 pr-2 pb-2 bg-white rounded-bl-md rounded-r-md shadow-md relative data-[has-errors=true]:ring data-[has-errors=true]:ring-red-100`}
          data-preview-resizer
          data-has-errors={queryAreaErrors && queryAreaErrors.length > 0}
        >
          <QueryEditor
            prefix={prefix}
            runPreview={runPreview}
            setSidebarOpen={setSidebarOpen}
            previewOpen={previewOpen}
            setPreviewOpen={setPreviewOpen}
            previewLoading={isLoadingPreview}
            disableDataViewImport={disableDataViewImport}
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
            readOnly={readOnly}
            previewColumnsSelectable={previewColumnsSelectable}
          />
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: "100%",
                }}
                exit={{
                  width: 0,
                  opacity: 0,
                }}
                className="absolute top-0 bottom-0 left-0 rounded-md bg-gray-50 shadow-md z-50 flex flex-col"
                transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              >
                <div className="p-5 w-full flex justify-between">
                  <h2 className="text-gray-800 text-xl font-medium">Data Options</h2>
                  <button
                    className="p-2 flex gap-2 text-gray-700"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSidebarOpen(false);
                    }}
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
                <hr className="mx-5 mb-10 border-gray-30" />
                <div className="flex-1 overflow-scroll p-1">
                  {!isEnrichment ? (
                    dataViews ? (
                      <SmartForm.RadioList<string>
                        name={`${fieldPrefix}dataViewId`}
                        disabled={readOnly}
                        columns
                        onChangeCallback={(value) => {
                          if (value) {
                            setValue(`${fieldPrefix}workflowTriggerType`, WorkflowTriggerType.DATA_VIEW, { shouldDirty: true });
                            const dataView = dataViews.find((dataView) => dataView.id === value);
                            monaco?.editor.getModels()[0].setValue(dataView?.query ?? "");
                            setValue(`${fieldPrefix}query`, dataView?.query ?? "", { shouldDirty: true });
                            setValue(
                              `${fieldPrefix}queryMeta`,
                              dataView?.queryMeta ?? {
                                includeHeaders: false,
                                isAggregation: false,
                              },
                              { shouldDirty: true },
                            );
                            utils.dataviews.get.invalidate({ dataViewId: value });
                          }
                        }}
                        options={dataViews.map((dataView: any) => {
                          return {
                            label: dataView.name,
                            value: dataView.id,
                            content: <DataViewSelectorListItem dataView={dataView} />,
                          };
                        })}
                      />
                    ) : (
                      <span>No Data views</span>
                    )
                  ) : null}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {queryAreaErrors && queryAreaErrors.length > 0 ? <div className="flex flex-col text-sm text-red-600 p-1">{queryAreaErrors}</div> : null}
      </div>

      {!disableRowFilters && !isEnrichment ? <RowFilterList queryColumns={queryColumns} readOnly={readOnly} /> : null}
    </div>
  );
}
